import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Utils
import { changeLanguage as i18nChangeLanguage, SupportedLanguage } from 'i18n';

interface UIState {
  isDarkMode: boolean;
  isMobile: boolean;
  language: SupportedLanguage;
  isSidebarMinimized: boolean;
}

const getInitialDarkMode = (): boolean => {
  const storedTheme = localStorage.getItem('nimax_theme');

  const isDark =
    storedTheme === 'dark' ||
    (!storedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const html = document.documentElement;

  if (isDark) {
    html.classList.add('dark');
  } else {
    html.classList.remove('dark');
  }

  return isDark;
};

const getInitialLanguage = (): SupportedLanguage => {
  const stored = localStorage.getItem('nimax_language');
  if (stored === 'en' || stored === 'es') {
    return stored;
  }
  return 'en';
};

const getInitialSidebarMinimized = (): boolean => {
  const stored = localStorage.getItem('nimax_sidebar_minimized');
  return stored === 'true';
};

const initialState: UIState = {
  isDarkMode: getInitialDarkMode(),
  isMobile: typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  language: getInitialLanguage(),
  isSidebarMinimized: getInitialSidebarMinimized(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleDarkMode: (state) => {
      state.isDarkMode = !state.isDarkMode;
      localStorage.setItem('nimax_theme', state.isDarkMode ? 'dark' : 'light');
      const html = document.documentElement;
      if (state.isDarkMode) {
        html.classList.add('dark');
      } else {
        html.classList.remove('dark');
      }
    },
    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload;
      localStorage.setItem('nimax_theme', action.payload ? 'dark' : 'light');
      const html = document.documentElement;
      if (action.payload) {
        html.classList.add('dark');
      } else {
        html.classList.remove('dark');
      }
    },
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },
    setLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
      state.language = action.payload;
      localStorage.setItem('nimax_language', action.payload);
      i18nChangeLanguage(action.payload);
    },
    toggleSidebarMinimized: (state) => {
      state.isSidebarMinimized = !state.isSidebarMinimized;
      localStorage.setItem('nimax_sidebar_minimized', state.isSidebarMinimized.toString());
    },
    setSidebarMinimized: (state, action: PayloadAction<boolean>) => {
      state.isSidebarMinimized = action.payload;
      localStorage.setItem('nimax_sidebar_minimized', action.payload.toString());
    },
  },
});

export const { toggleDarkMode, setDarkMode, setIsMobile, setLanguage, toggleSidebarMinimized, setSidebarMinimized } = uiSlice.actions;
export default uiSlice.reducer;

